import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientDto, RunningSessionDto, SessionDto, SessionNoteDto, SessionType, UserDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { dayLabel, elapsed, euro, fmtClock, fmtLongDate, fmtTime, hm, localDay, monthLabel, rateShort, thisMonth, todayKey, typeLabel } from '../format';
import {
  AddInput,
  Avatar,
  Calendar,
  ClockCard,
  ContextMenu,
  Empty,
  ErrorBanner,
  ManualSessionModal,
  Modal,
  NoteRow,
  Panel,
  Row,
  Section,
  Seg,
  SessionMetaModal,
  SessionsList,
  Stat,
  TimeEditModal,
  useMenu,
  useNow,
  useSections,
} from '../ui';

type Filter = 'live' | 'month' | 'day';

/** Dashboard del consulente: agenda (sessione, orologio, calendario) e attività (note, clienti, sessioni). */
export function ConsultantView({ ctx }: { ctx: Ctx }) {
  const { api, event, eventSeq } = ctx;
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [contacts, setContacts] = useState<UserDto[]>([]);
  const [current, setCurrent] = useState<RunningSessionDto | null>(null);
  const [lastEnded, setLastEnded] = useState<SessionDto | null>(null);
  const [month, setMonth] = useState(thisMonth());
  const [sessions, setSessions] = useState<SessionDto[]>([]);
  const [filter, setFilter] = useState<Filter>('live');
  const [day, setDay] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [stopOpen, setStopOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [toDelete, setToDelete] = useState<SessionDto | null>(null);
  const [toEdit, setToEdit] = useState<SessionDto | null>(null);
  const [editStart, setEditStart] = useState(false);
  const [meta, setMeta] = useState<{ session: SessionDto; running: boolean } | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sections = useSections({ notes: true, today: false, clients: true });
  const menu = useMenu();
  const now = useNow(1000);
  const today = todayKey();

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError((e as Error).message);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  const loadCurrent = useCallback(() => api.get<RunningSessionDto | null>('/api/sessions/current').then(setCurrent), [api]);

  const loadBase = useCallback(async () => {
    const [c, u] = await Promise.all([api.get<ClientDto[]>('/api/admin/clients'), api.get<UserDto[]>('/api/admin/users?role=client')]);
    setClients(c);
    setContacts(u);
    await loadCurrent();
  }, [api, loadCurrent]);

  const loadSessions = useCallback(async () => {
    const rows = await api.get<SessionDto[]>(`/api/admin/sessions?month=${month}&limit=500`);
    setSessions(rows);
  }, [api, month]);

  useEffect(() => {
    run(loadBase);
  }, [run, loadBase]);

  useEffect(() => {
    run(loadSessions);
  }, [run, loadSessions]);

  // Eventi in tempo reale (utile se si usa l'app da più PC).
  useEffect(() => {
    if (!event) return;
    if (event.type === 'session.started' || event.type === 'session.stopped') {
      loadCurrent().catch(() => undefined);
      loadSessions().catch(() => undefined);
    } else if (event.type === 'note.added') {
      const added = event.note;
      setCurrent((c) =>
        c && c.id === added.sessionId && !c.notes.some((n) => n.id === added.id) ? { ...c, notes: [...c.notes, added] } : c,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSeq]);

  // Stima dell'importo aggiornata ogni minuto durante la sessione.
  useEffect(() => {
    if (!current) return;
    const t = setInterval(() => loadCurrent().catch(() => undefined), 60_000);
    return () => clearInterval(t);
  }, [current?.id, loadCurrent]);

  useEffect(() => {
    if (current) window.tt.setTray('running', `In sessione con ${current.clientName} da ${fmtTime(current.startedAt)}`);
    else window.tt.setTray('idle', 'CRM TecnoSide — nessuna sessione attiva');
  }, [current?.id, current?.clientName]);

  const start = (clientId: number, type: SessionType) =>
    run(async () => {
      await api.post<SessionDto>('/api/sessions/start', { clientId, type });
      setLastEnded(null);
      setFilter('live');
      await loadCurrent();
    });

  const stop = () =>
    run(async () => {
      const ended = await api.post<SessionDto>('/api/sessions/stop', { summary });
      setCurrent(null);
      setStopOpen(false);
      setSummary('');
      setLastEnded(ended);
      await loadSessions();
    });

  /** Sposta l'inizio della sessione in corso (cronometro avviato in ritardo). */
  const saveRunningStart = async (startedAt: string) => {
    const updated = await api.request<RunningSessionDto>('PATCH', '/api/sessions/current/start', { startedAt });
    setCurrent(updated);
  };

  /** Corregge gli orari di una sessione conclusa: ore e importo vengono rifatti. */
  const saveSessionTimes = async (id: number, startedAt: string, endedAt: string | null) => {
    await api.request<SessionDto>('PATCH', `/api/sessions/${id}/times`, { startedAt, endedAt });
    await loadSessions();
  };

  /** Cliente, tipo e riepilogo (la sessione viene ricalcolata se cambia cliente). */
  const saveMeta = async (session: SessionDto, running: boolean, patch: Record<string, unknown>) => {
    await api.request<SessionDto>('PATCH', `/api/sessions/${session.id}`, patch);
    if (running) await loadCurrent();
    else await loadSessions();
  };

  /** Sessione dimenticata, inserita a mano. */
  const addManual = async (input: Record<string, unknown>) => {
    await api.post<SessionDto>('/api/sessions/manual', input);
    await loadSessions();
  };

  /** Avvio per sbaglio: la sessione in corso viene buttata via. */
  const discard = () =>
    run(async () => {
      await api.post('/api/sessions/current/discard', {});
      setDiscardOpen(false);
      setCurrent(null);
    });

  const runningMenu = (e: React.MouseEvent) => {
    if (!current) return;
    menu.open(
      e,
      [
        { icon: 'clock', label: "Correggi l'orario di inizio", onClick: () => setEditStart(true) },
        { icon: 'edit', label: 'Cambia cliente o tipo', onClick: () => setMeta({ session: current, running: true }) },
        { icon: 'stop', label: 'Termina sessione', onClick: () => setStopOpen(true), sep: true },
        { icon: 'trash', label: 'Annulla: non registrarla', onClick: () => setDiscardOpen(true), danger: true },
      ],
      `${current.clientName} \u00b7 in corso`,
    );
  };

  const sessionMenu = (s: SessionDto, e: React.MouseEvent) => {
    menu.open(
      e,
      [
        { icon: 'clock', label: 'Correggi orari e durata', onClick: () => setToEdit(s) },
        { icon: 'edit', label: 'Cliente, tipo e riepilogo', onClick: () => setMeta({ session: s, running: false }) },
        {
          icon: 'copy',
          label: 'Copia riepilogo',
          sep: true,
          onClick: () =>
            navigator.clipboard.writeText(
              `${s.clientName} \u00b7 ${fmtTime(s.startedAt)}-${s.endedAt ? fmtTime(s.endedAt) : ''} \u00b7 ${hm(s.totalMinutes)} \u00b7 ${euro(s.amountCents)}${s.summary ? `\n${s.summary}` : ''}`,
            ),
        },
        { icon: 'plus', label: 'Aggiungi una sessione a mano', onClick: () => setManualOpen(true) },
        { icon: 'trash', label: 'Elimina sessione', onClick: () => setToDelete(s), danger: true, sep: true },
      ],
      `${s.clientName} \u00b7 ${fmtTime(s.startedAt)}`,
    );
  };

  const AddManualButton = () => (
    <div className="hstack" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
      <button type="button" className="btn small add-manual" title="Per le ore che hai fatto senza avviare il conteggio" onClick={() => setManualOpen(true)}>
        <Icon name="plus" size={14} /> Aggiungi sessione a mano
      </button>
    </div>
  );

  const sendNote = () => {
    const body = note.trim();
    if (!body) return;
    run(async () => {
      const n = await api.post<SessionNoteDto>('/api/sessions/current/notes', { body });
      setNote('');
      setCurrent((c) => (c ? { ...c, notes: c.notes.some((x) => x.id === n.id) ? c.notes : [...c.notes, n] } : c));
    });
  };

  const remove = (s: SessionDto) =>
    run(async () => {
      await api.del(`/api/admin/sessions/${s.id}`);
      setToDelete(null);
      await loadSessions();
    });

  const byDay = useMemo(() => {
    const m: Record<string, SessionDto[]> = {};
    for (const s of sessions) (m[localDay(s.startedAt)] ??= []).push(s);
    return m;
  }, [sessions]);

  const marks = useMemo(() => {
    const m: Record<string, true> = {};
    for (const k of Object.keys(byDay)) m[k] = true;
    if (current) m[today] = true;
    return m;
  }, [byDay, current, today]);

  const todaySessions = month === thisMonth() ? (byDay[today] ?? []) : [];
  const listed = filter === 'day' && day ? (byDay[day] ?? []) : sessions;
  const listedMinutes = listed.reduce((a, s) => a + s.totalMinutes, 0);
  const listedAmount = listed.reduce((a, s) => a + s.amountCents, 0);

  const goToday = () => {
    setMonth(thisMonth());
    setDay(today);
    setFilter('day');
  };
  const pickDay = (k: string) => {
    if (k.slice(0, 7) !== month) setMonth(k.slice(0, 7));
    setDay(k);
    setFilter('day');
  };

  const activeClients = clients.filter((c) => c.active);
  const contactNames = (clientId: number) =>
    contacts
      .filter((u) => u.clientId === clientId && u.active)
      .map((u) => u.name)
      .join(', ');

  const subtitle =
    filter === 'live'
      ? current
        ? `In corso · ${current.clientName}`
        : 'Nessuna sessione attiva'
      : filter === 'day' && day
        ? dayLabel(day)
        : monthLabel(month);

  return (
    <div className="panels">
      <Panel title="Ore di lavoro" subtitle="Avvia il conteggio quando lavori per un cliente">
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <div>
          <Seg icon="inbox" label="In corso" count={current ? 1 : 0} tone={current ? 'good' : undefined} active={filter === 'live'} onClick={() => setFilter('live')} />
          <Seg icon="checkCircle" label="Oggi" count={todaySessions.length + (current ? 1 : 0)} active={filter === 'day' && day === today} onClick={goToday} />
          <Seg icon="layers" label="Tutto il mese" count={sessions.length} active={filter === 'month'} onClick={() => setFilter('month')} />
        </div>

        <ClockCard
          now={now}
          on={!!current}
          headline={current ? elapsed(current.startedAt, now) : fmtClock(now)}
          sub={
            current ? (
              <>
                <b>{current.clientName}</b> · ≈ {euro(current.preview.amountCents)}
              </>
            ) : (
              fmtLongDate(now)
            )
          }
        />

        <Calendar
          month={month}
          selected={filter === 'day' ? day : null}
          marks={marks}
          onSelect={pickDay}
          onMonth={(m) => {
            setMonth(m);
            setDay(null);
            setFilter('month');
          }}
        />
      </Panel>

      <Panel title="Attività" subtitle={subtitle}>
        {filter === 'live' && current && (
          <>
            <div className="card navy" onContextMenu={runningMenu}>
              <div className="hstack between wrap">
                <div>
                  <div className="eyebrow" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {typeLabel(current.type)} · dalle {fmtTime(current.startedAt)}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 500 }}>{current.clientName}</div>
                  {(() => {
                    const c = clients.find((x) => x.id === current.clientId);
                    return c ? (
                      <div className="small" style={{ color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                        Tariffa {rateShort(c)}
                      </div>
                    ) : null;
                  })()}
                  <div className="hstack" style={{ gap: 6, marginTop: 10 }}>
                    <button type="button" className="chip on-navy" onClick={() => setEditStart(true)}>
                      <Icon name="edit" size={14} /> Modifica orario di inizio
                    </button>
                    <button type="button" className="chip on-navy" title="Altre azioni (o tasto destro sulla scheda)" onClick={runningMenu}>
                      <Icon name="dots" size={14} /> Altro
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="timer" style={{ fontSize: 28, fontWeight: 500 }}>
                    {elapsed(current.startedAt, now)}
                  </div>
                  <div className="muted small">
                    ≈ {euro(current.preview.amountCents)} · {hm(current.preview.totalMinutes)} fatturabili
                  </div>
                </div>
              </div>
            </div>

            <AddInput value={note} onChange={setNote} onSubmit={sendNote} placeholder="Aggiungi nota, premi" disabled={busy} autoFocus />

            <Section title="Note" count={current.notes.length} open={sections.open.notes} onToggle={() => sections.toggle('notes')}>
              {current.notes.length === 0 ? (
                <Empty icon="note">Scrivi cosa stai facendo: il cliente lo vede in tempo reale e finisce nel report.</Empty>
              ) : (
                current.notes.map((n) => <NoteRow key={n.id} note={n} />)
              )}
            </Section>

            <div className="hstack" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn danger" onClick={() => setStopOpen(true)} disabled={busy} title="Ferma il conteggio: prima di chiudere puoi scrivere un riepilogo per il cliente">
                <Icon name="stop" size={16} /> Termina sessione
              </button>
            </div>

            <Section title="Sessioni di oggi" count={todaySessions.length} open={sections.open.today} onToggle={() => sections.toggle('today')}>
              <AddManualButton />
              <SessionsList
                api={api}
                sessions={todaySessions}
                onDelete={setToDelete}
                onEdit={setToEdit}
                onMenu={sessionMenu}
                emptyText="Nessuna sessione conclusa oggi."
              />
            </Section>
          </>
        )}

        {filter === 'live' && !current && (
          <>
            {lastEnded && (
              <div className="card">
                <div className="eyebrow">Ultima sessione</div>
                <div className="hstack between">
                  <span>
                    <b>{lastEnded.clientName}</b> · {typeLabel(lastEnded.type)}
                  </span>
                  <span className="pill">
                    {hm(lastEnded.totalMinutes)} · {euro(lastEnded.amountCents)}
                  </span>
                </div>
              </div>
            )}
            <Section title="Avvia con un cliente" count={activeClients.length} open={sections.open.clients} onToggle={() => sections.toggle('clients')}>
              {activeClients.length === 0 ? (
                <Empty icon="building">Nessun cliente attivo. Creane uno dalla pagina Clienti.</Empty>
              ) : (
                activeClients.map((c) => (
                  <Row key={c.id}>
                    <Avatar name={c.name} />
                    <div className="grow">
                      <div className="title">{c.name}</div>
                      <div className="sub">
                        {rateShort(c)}
                        {contactNames(c.id) ? ` · ${contactNames(c.id)}` : ''}
                      </div>
                    </div>
                    <div className="actions">
                      <button type="button" className="btn small dark" disabled={busy} title="Fa partire il conteggio del tempo per questo cliente" onClick={() => start(c.id, 'work')}>
                        <Icon name="play" size={14} /> Avvia
                      </button>
                      <button type="button" className="btn small" disabled={busy} title="Conta il tempo di una telefonata con questo cliente" onClick={() => start(c.id, 'call')}>
                        <Icon name="phone" size={14} /> Telefonata
                      </button>
                    </div>
                  </Row>
                ))
              )}
            </Section>
            <Section title="Sessioni di oggi" count={todaySessions.length} open={sections.open.today} onToggle={() => sections.toggle('today')}>
              <AddManualButton />
              <SessionsList
                api={api}
                sessions={todaySessions}
                onDelete={setToDelete}
                onEdit={setToEdit}
                onMenu={sessionMenu}
                emptyText="Nessuna sessione conclusa oggi."
              />
            </Section>
          </>
        )}

        {filter !== 'live' && (
          <>
            <div className="stats">
              <Stat value={listed.length} label="sessioni" />
              <Stat value={hm(listedMinutes)} label="ore" />
              <Stat value={euro(listedAmount)} label="importo" navy />
            </div>
            <AddManualButton />
            <SessionsList api={api} sessions={listed} onDelete={setToDelete} onEdit={setToEdit} onMenu={sessionMenu} />
          </>
        )}
      </Panel>

      {stopOpen && current && (
        <Modal
          title={`Termina sessione con ${current.clientName}`}
          onClose={() => setStopOpen(false)}
          narrow
          footer={
            <>
              <button type="button" className="btn" onClick={() => setStopOpen(false)} disabled={busy}>
                Continua a lavorare
              </button>
              <button type="button" className="btn danger" onClick={stop} disabled={busy}>
                <Icon name="stop" size={16} /> Termina e genera report
              </button>
            </>
          }
        >
          <div className="muted small mb" title="Il report va al cliente con le note scritte durante il lavoro.">
            Durata {elapsed(current.startedAt, now)} · stima {euro(current.preview.amountCents)} · {current.notes.length === 1 ? '1 nota' : `${current.notes.length} note`} nel report
          </div>
          <textarea
            autoFocus
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Riepilogo per il cliente (facoltativo): cosa è stato fatto, cosa resta da fare…"
          />
        </Modal>
      )}

      {editStart && current && (
        <TimeEditModal
          title="Correggi l'orario di inizio"
          startedAt={current.startedAt}
          onClose={() => setEditStart(false)}
          onSave={(startedAt) => saveRunningStart(startedAt)}
        />
      )}

      {toEdit && (
        <TimeEditModal
          title={`Orari · ${toEdit.clientName}`}
          startedAt={toEdit.startedAt}
          endedAt={toEdit.endedAt ?? toEdit.startedAt}
          onClose={() => setToEdit(null)}
          onSave={(startedAt, endedAt) => saveSessionTimes(toEdit.id, startedAt, endedAt)}
        />
      )}

      {meta && (
        <SessionMetaModal
          session={meta.session}
          clients={clients}
          running={meta.running}
          onClose={() => setMeta(null)}
          onSave={(patch) => saveMeta(meta.session, meta.running, patch)}
        />
      )}

      {manualOpen && <ManualSessionModal clients={clients} onClose={() => setManualOpen(false)} onSave={addManual} />}

      {discardOpen && current && (
        <Modal
          title="Annullare la sessione in corso?"
          onClose={() => setDiscardOpen(false)}
          narrow
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDiscardOpen(false)} disabled={busy}>
                No, continua
              </button>
              <button type="button" className="btn danger" onClick={discard} disabled={busy}>
                <Icon name="trash" size={16} /> Annulla sessione
              </button>
            </>
          }
        >
          <div className="small">
            {current.clientName} · avviata alle {fmtTime(current.startedAt)} · {elapsed(current.startedAt, now)}
          </div>
          <div className="muted small mt">Non resta da nessuna parte: serve se l’hai avviata per sbaglio.</div>
        </Modal>
      )}

      <ContextMenu menu={menu.menu} onClose={menu.close} />

      {toDelete && (
        <Modal
          title="Eliminare la sessione?"
          onClose={() => setToDelete(null)}
          narrow
          footer={
            <>
              <button type="button" className="btn" onClick={() => setToDelete(null)} disabled={busy}>
                Annulla
              </button>
              <button type="button" className="btn danger" onClick={() => remove(toDelete)} disabled={busy}>
                <Icon name="trash" size={16} /> Elimina
              </button>
            </>
          }
        >
          <div className="small">
            {toDelete.clientName} · {fmtTime(toDelete.startedAt)}
            {toDelete.endedAt ? `–${fmtTime(toDelete.endedAt)}` : ''} · {hm(toDelete.totalMinutes)} · {euro(toDelete.amountCents)}
          </div>
          <div className="muted small mt">Sparisce dai report e dalle provvigioni. Non si può annullare.</div>
        </Modal>
      )}
    </div>
  );
}
